import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { BookOpen, CheckCircle2, Moon, Sun } from 'lucide-react'
import { articlesApi } from '@/api/articles'
import { settingsApi } from '@/api/settings'
import { Button } from '@/components/ui/Button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card'
import { Label } from '@/components/ui/Label'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { cn } from '@/components/ui/cn'
import { formatMessage } from '@/i18n/format'
import { useMinCopy } from '@/i18n/useUiLocale'
import { getApiErrorMessage } from '@/lib/apiError'
import { GUEST_LOCALES, getGuestLocale, isGuestLocale, setGuestLocale, type GuestLocale } from '@/lib/guestLocale'
import { useSettingsStore } from '@/store/settingsStore'
import { clearReviewSession } from '@/lib/reviewSessionStorage'
import { CEFR_LEVEL_OPTIONS, NATIVE_LANGUAGE_OPTIONS } from '@/types/settings'
import type { CefrLevel } from '@/types/common'

const STEPS = ['native', 'theme', 'level', 'duration', 'topics', 'complete'] as const

function detectTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

function defaultNativeLanguage(uiLocale: GuestLocale): string {
  if (uiLocale !== 'en' && NATIVE_LANGUAGE_OPTIONS.some((o) => o.code === uiLocale)) {
    return uiLocale
  }
  return 'ru'
}

export function WelcomeOnboardingPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const setSettings = useSettingsStore((s) => s.setSettings)
  const setTheme = useSettingsStore((s) => s.setTheme)
  const storeTheme = useSettingsStore((s) => s.theme)
  const copy = useMinCopy({ preferGuest: true })
  const t = copy.onboarding
  const themeCopy = copy.settings

  const [stepIndex, setStepIndex] = useState(0)
  const [uiLanguage, setUiLanguage] = useState<GuestLocale>(() => getGuestLocale())
  const [nativeLanguage, setNativeLanguage] = useState(() => defaultNativeLanguage(getGuestLocale()))
  const [theme, setThemeLocal] = useState<'light' | 'dark'>(() => storeTheme)
  const [targetEnglishLevel, setTargetEnglishLevel] = useState<CefrLevel>('B1')
  const [sessionDurationMinutes, setSessionDurationMinutes] = useState(8)
  const [preferredTags, setPreferredTags] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)

  const step = STEPS[stepIndex]

  const tagsQuery = useQuery({
    queryKey: ['article-tags'],
    queryFn: () => articlesApi.getTags().then((r) => r.data),
  })

  const completeMutation = useMutation({
    mutationFn: () =>
      settingsApi.updateSettings({
        nativeLanguage,
        uiLanguage,
        theme,
        targetEnglishLevel,
        sessionDurationMinutes,
        preferredTags,
        timezone: detectTimezone(),
        onboardingCompleted: true,
      }),
    onSuccess: (updated) => {
      setSettings(updated)
      if (isGuestLocale(updated.uiLanguage)) {
        setGuestLocale(updated.uiLanguage)
      }
      queryClient.setQueryData(['settings'], updated)
      clearReviewSession()
      queryClient.invalidateQueries({ queryKey: ['vocabulary'] })
      queryClient.invalidateQueries({ queryKey: ['vocabularyPhrases'] })
      queryClient.invalidateQueries({ queryKey: ['knownPhrases'] })
      queryClient.invalidateQueries({ queryKey: ['vocabularyStats'] })
      setStepIndex(STEPS.indexOf('complete'))
    },
    onError: (err: unknown) => {
      setError(getApiErrorMessage(err, t.saveFailed))
    },
  })

  const handleUiLanguageChange = (next: GuestLocale) => {
    setUiLanguage(next)
    setGuestLocale(next)
  }

  const handleThemeChange = (next: 'light' | 'dark') => {
    setThemeLocal(next)
    setTheme(next)
  }

  const toggleTag = (tag: string) => {
    setPreferredTags((current) =>
      current.includes(tag) ? current.filter((value) => value !== tag) : [...current, tag],
    )
  }

  const goNext = () => {
    setError(null)
    if (step === 'topics') {
      completeMutation.mutate()
      return
    }
    setStepIndex((index) => Math.min(index + 1, STEPS.length - 1))
  }

  const goBack = () => {
    setError(null)
    setStepIndex((index) => Math.max(index - 1, 0))
  }

  const handleFinish = () => {
    navigate('/learning-demo', { replace: true })
  }

  const contentSteps = STEPS.length - 1
  const progressStep = step === 'complete' ? contentSteps : stepIndex + 1

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-10">
      <Card className="w-full max-w-lg" data-testid="onboarding-wizard">
        <CardHeader className="text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
            {step === 'complete' ? (
              <CheckCircle2 className="h-6 w-6" />
            ) : (
              <BookOpen className="h-6 w-6" />
            )}
          </div>
          <CardTitle>
            {step === 'complete' ? t.stepCompleteTitle : t.wizardTitle}
          </CardTitle>
          <CardDescription>
            {step === 'complete' ? t.stepCompleteDescription : t.wizardSubtitle}
          </CardDescription>
          {step !== 'complete' ? (
            <div className="mx-auto mt-4 flex w-full max-w-xs gap-1.5">
              {STEPS.slice(0, -1).map((_, index) => (
                <div
                  key={index}
                  className={cn(
                    'h-1.5 flex-1 rounded-full transition-colors',
                    index < progressStep ? 'bg-primary' : 'bg-muted',
                  )}
                />
              ))}
            </div>
          ) : null}
        </CardHeader>

        <CardContent className="space-y-6">
          {step === 'native' ? (
            <div className="space-y-6" data-testid="onboarding-step-native">
              <div className="space-y-3">
                <div>
                  <h2 className="text-sm font-semibold">{t.stepUiTitle}</h2>
                  <p className="mt-1 text-sm text-muted-foreground">{t.stepUiDescription}</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="onboarding-ui-language">{t.uiLanguageLabel}</Label>
                  <select
                    id="onboarding-ui-language"
                    data-testid="onboarding-ui-language-select"
                    value={uiLanguage}
                    onChange={(e) => handleUiLanguageChange(e.target.value as GuestLocale)}
                    className="flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    {GUEST_LOCALES.map((opt) => (
                      <option key={opt.code} value={opt.code}>
                        {opt.nativeLabel}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-3">
                <div>
                  <h2 className="text-sm font-semibold">{t.stepNativeTitle}</h2>
                  <p className="mt-1 text-sm text-muted-foreground">{t.stepNativeDescription}</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="onboarding-native">{t.languageLabel}</Label>
                  <select
                    id="onboarding-native"
                    data-testid="onboarding-native-select"
                    value={nativeLanguage}
                    onChange={(e) => setNativeLanguage(e.target.value)}
                    className="flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    {NATIVE_LANGUAGE_OPTIONS.map((opt) => (
                      <option key={opt.code} value={opt.code}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          ) : null}

          {step === 'theme' ? (
            <div className="space-y-3" data-testid="onboarding-step-theme">
              <div>
                <h2 className="text-sm font-semibold">{t.stepThemeTitle}</h2>
                <p className="mt-1 text-sm text-muted-foreground">{t.stepThemeDescription}</p>
              </div>
              <div className="space-y-2">
                <Label>{themeCopy.themeLabel}</Label>
                <div className="flex gap-3">
                  <button
                    type="button"
                    data-testid="onboarding-theme-light"
                    onClick={() => handleThemeChange('light')}
                    className={cn(
                      'flex flex-1 items-center justify-center gap-2 rounded-lg border-2 p-4 text-sm font-medium transition-colors',
                      theme === 'light'
                        ? 'border-primary bg-primary/5 text-primary'
                        : 'border-border bg-background text-muted-foreground hover:border-muted-foreground/50',
                    )}
                  >
                    <Sun className="h-4 w-4" />
                    {themeCopy.themeLight}
                  </button>
                  <button
                    type="button"
                    data-testid="onboarding-theme-dark"
                    onClick={() => handleThemeChange('dark')}
                    className={cn(
                      'flex flex-1 items-center justify-center gap-2 rounded-lg border-2 p-4 text-sm font-medium transition-colors',
                      theme === 'dark'
                        ? 'border-primary bg-primary/5 text-primary'
                        : 'border-border bg-background text-muted-foreground hover:border-muted-foreground/50',
                    )}
                  >
                    <Moon className="h-4 w-4" />
                    {themeCopy.themeDark}
                  </button>
                </div>
              </div>
            </div>
          ) : null}

          {step === 'level' ? (
            <div className="space-y-3" data-testid="onboarding-step-level">
              <div>
                <h2 className="text-sm font-semibold">{t.stepLevelTitle}</h2>
                <p className="mt-1 text-sm text-muted-foreground">{t.stepLevelDescription}</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="onboarding-level">{t.levelLabel}</Label>
                <select
                  id="onboarding-level"
                  data-testid="onboarding-level-select"
                  value={targetEnglishLevel}
                  onChange={(e) => setTargetEnglishLevel(e.target.value as CefrLevel)}
                  className="flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {CEFR_LEVEL_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          ) : null}

          {step === 'duration' ? (
            <div className="space-y-3" data-testid="onboarding-step-duration">
              <div>
                <h2 className="text-sm font-semibold">{t.stepDurationTitle}</h2>
                <p className="mt-1 text-sm text-muted-foreground">{t.stepDurationDescription}</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="onboarding-duration">
                  {formatMessage(t.stepDurationLabel, { minutes: sessionDurationMinutes })}
                </Label>
                <input
                  id="onboarding-duration"
                  data-testid="onboarding-duration-slider"
                  type="range"
                  min={5}
                  max={15}
                  step={1}
                  value={sessionDurationMinutes}
                  onChange={(e) => setSessionDurationMinutes(Number(e.target.value))}
                  className="w-full accent-primary"
                />
              </div>
            </div>
          ) : null}

          {step === 'topics' ? (
            <div className="space-y-3" data-testid="onboarding-step-topics">
              <div>
                <h2 className="text-sm font-semibold">{t.stepTopicsTitle}</h2>
                <p className="mt-1 text-sm text-muted-foreground">{t.stepTopicsDescription}</p>
              </div>
              {tagsQuery.isLoading ? (
                <div className="flex justify-center py-6">
                  <LoadingSpinner />
                </div>
              ) : tagsQuery.data && tagsQuery.data.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {tagsQuery.data.map((tag) => (
                    <button
                      key={tag}
                      type="button"
                      data-testid={`onboarding-topic-${tag}`}
                      onClick={() => toggleTag(tag)}
                      className={cn(
                        'rounded-full border px-3 py-1.5 text-xs font-medium capitalize transition-colors',
                        preferredTags.includes(tag)
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-border text-muted-foreground hover:border-primary/40',
                      )}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">{t.stepTopicsSkip}</p>
              )}
            </div>
          ) : null}

          {step === 'complete' ? (
            <div className="space-y-4 text-center" data-testid="onboarding-complete">
              <p className="text-sm text-muted-foreground">{t.stepCompleteHint}</p>
              <Button className="w-full" onClick={handleFinish} data-testid="onboarding-finish">
                {t.finish}
              </Button>
            </div>
          ) : null}

          {error ? <p className="text-sm text-destructive">{error}</p> : null}

          {step !== 'complete' ? (
            <div className="flex items-center gap-3">
              {stepIndex > 0 ? (
                <Button
                  type="button"
                  className="w-auto flex-1 bg-secondary text-secondary-foreground hover:bg-secondary/80"
                  onClick={goBack}
                  data-testid="onboarding-back"
                >
                  {t.back}
                </Button>
              ) : null}
              <Button
                type="button"
                className={cn(stepIndex === 0 ? 'w-full' : 'flex-1')}
                onClick={goNext}
                loading={completeMutation.isPending}
                data-testid="onboarding-next"
              >
                {step === 'topics' ? t.finish : t.next}
              </Button>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  )
}
